import { useState, useEffect } from 'react'
import toast from 'react-hot-toast'
import { useProspectsSearch } from '../hooks/useProspectsSearch.js'
import { useProspectSearchStore } from '../stores/prospectSearchStore.js'
import { useProspectHistory } from '../hooks/useProspectHistory.js'
import { useProspectEnrichment } from '../hooks/useProspectEnrichment.js'
import { useLocalProspectsSearch } from '../hooks/useLocalProspectsSearch.js'
import { localSearchProspects, claimProspects } from '../services/api.js'
import ProspectsSearchBox from '../components/prospects/ProspectsSearchBox.jsx'
import ProspectsEmptyState from '../components/prospects/ProspectsEmptyState.jsx'
import LoadingAnimation, { SearchingAnimation } from '../components/prospects/LoadingAnimation.jsx'
import ProspectsPreviewGrid from '../components/prospects/ProspectsPreviewGrid.jsx'
import EnrichmentSelector from '../components/prospects/EnrichmentSelector.jsx'
import SearchHistory from '../components/prospects/SearchHistory.jsx'
import ProspectsTableCRM from '../components/prospects/ProspectsTableCRM.jsx'
import { useAuthStore } from '../stores/authStore.js'
import { isAdmin } from '../features/admin/utils/isAdmin.js'

export default function Prospects() {
  const { loading, error, results, progress, found, search, reset, setResults } = useProspectsSearch()
  const { loading: historyLoading, searches, load: loadHistory } = useProspectHistory(true) // autoLoad = true
  const { loading: enrichLoading, enrich } = useProspectEnrichment()
  const [showLoadingAnimation, setShowLoadingAnimation] = useState(false)
  const [showPreview, setShowPreview] = useState(false)
  const storeQuery = useProspectSearchStore((s) => s.currentQuery)
  const [currentQuery, setCurrentQuery] = useState(storeQuery || '')
  const [currentForm, setCurrentForm] = useState({ jobTitle: '', location: '', sector: '' })
  const [crmView, setCrmView] = useState(null) // { search, prospects }
  const [desiredQuantity, setDesiredQuantity] = useState(5)

  const { localResults } = useLocalProspectsSearch(currentForm)
  const admin = isAdmin(useAuthStore((s) => s.user))

  // Trigger loading animation when results arrive
  useEffect(() => {
    if (!loading && results && !showLoadingAnimation && !showPreview) {
      setShowLoadingAnimation(true)
    }
  }, [loading, results, showLoadingAnimation, showPreview])

  const handleSearch = async (query, quantity = 5, form = {}) => {
    const newForm = { jobTitle: form.jobTitle || '', location: form.location || '', sector: form.sector || '' }
    setCurrentQuery(query)
    setCurrentForm(newForm)
    setDesiredQuantity(quantity)
    setShowPreview(false)
    setShowLoadingAnimation(false)
    setCrmView(null)
    reset()

    try {
      const localData = await localSearchProspects(newForm)
      const found = localData?.prospects || []

      if (found.length >= quantity) {
        const sliced = found.slice(0, quantity)
        setResults({
          prospects: sliced,
          total: sliced.length,
          requestId: `db_${Date.now()}`,
          availableContactIds: sliced.map(p => String(p.id)),
          fromDb: true,
        })
        return
      }
    } catch {
    }

    search(query, quantity)
  }

  const handleSearchExternal = () => {
    setCrmView(null)
    search(currentQuery, desiredQuantity)
  }

  const handleOpenLocalCrm = () => {
    setCrmView({ search: { query: currentQuery }, prospects: localResults })
  }

  const handleLoadingComplete = () => {
    setShowLoadingAnimation(false)
    setShowPreview(true)
    // Reload history to include new search
    loadHistory()
  }

  const handleEnrich = async (options) => {
    if (!results?.requestId || !results?.availableContactIds) {
      toast.error('Données de recherche manquantes')
      return
    }

    if (results.fromDb) {
      if (!confirm(`Confirmer l'achat de ${options.quantity} prospects ?\n\nCoût : ${options.creditsNeeded} crédits`)) return
      try {
        const ids = results.availableContactIds.slice(0, options.quantity)
        const res = await claimProspects(ids)
        toast.success(`${res.claimed_count} prospects ajoutés à votre compte !`, { duration: 4000 })
        loadHistory()
        handleCancel()
      } catch (err) {
        toast.error(`Erreur : ${err.message}`)
      }
      return
    }

    if (
      !confirm(
        `Confirmer l'enrichissement de ${options.quantity.toLocaleString()} prospects ?\n\nCoût : ${options.creditsNeeded.toLocaleString()} crédits`
      )
    ) {
      return
    }

    try {
      // Get the first N contactIds based on quantity
      const contactIdsToEnrich = results.availableContactIds.slice(0, options.quantity)

      const enrichOptions = {
        requireEmail: options.requireEmail,
        revealEmails: options.revealEmails,
        revealPhones: options.revealPhones,
      }

      const result = await enrich(results.requestId, contactIdsToEnrich, options.quantity, enrichOptions)

      toast.success(
        `${result.enriched_count} prospects enrichis avec succès !\nCrédits débités: ${result.credits_deducted}`,
        { duration: 4000 }
      )

      // Reload history and reset search
      loadHistory()
      handleCancel()
    } catch (err) {
      toast.error(`Erreur d'enrichissement: ${err.message}`)
    }
  }

  const handleCancel = () => {
    reset()
    setShowPreview(false)
    setShowLoadingAnimation(false)
  }

  const handleSelectHistory = (search) => {
    // Afficher la vue CRM avec les prospects de l'historique
    const previewProspects = search.metadata?.preview_prospects || []
    
    if (previewProspects.length === 0) {
      toast.error('Les données d\'aperçu ne sont plus disponibles pour cette recherche.')
      return
    }

    setCrmView({
      search,
      prospects: previewProspects,
    })
  }

  const handleCloseCrmView = () => {
    setCrmView(null)
  }

  const handleEnrichSingle = async (contactId, options) => {
    if (!crmView?.search?.requestId) {
      toast.error('Données de recherche manquantes')
      return
    }

    if (!confirm(`Enrichir ce prospect ?\n\nCoût: 2 crédits`)) {
      return
    }

    try {
      const enrichOptions = {
        requireEmail: false,
        revealEmails: options.emailOnly || options.both,
        revealPhones: options.phoneOnly || options.both,
      }

      const result = await enrich(crmView.search.requestId, [contactId], 1, enrichOptions)

      toast.success(
        `1 prospect enrichi avec succès !\nCrédits débités: ${result.credits_deducted}`,
        { duration: 3000 }
      )

      // Reload history
      loadHistory()
    } catch (err) {
      toast.error(`Erreur: ${err.message}`)
    }
  }

  const handleBuyMore = async (search, remaining) => {
    if (!search.requestId || !search.availableContactIds) {
      toast.error('Impossible d\'acheter des prospects supplémentaires pour cette recherche')
      return
    }

    const quantity = Math.min(100, remaining)
    const creditsNeeded = quantity * 2

    if (!confirm(`Acheter ${quantity} prospects supplémentaires ?\n\nCoût: ${creditsNeeded} crédits`)) {
      return
    }

    try {
      // Get contactIds that haven't been enriched yet
      const alreadyEnriched = search.enrichedCount || 0
      const contactIdsToEnrich = search.availableContactIds.slice(alreadyEnriched, alreadyEnriched + quantity)

      const result = await enrich(search.requestId, contactIdsToEnrich, quantity, {
        requireEmail: false,
        revealEmails: true,
        revealPhones: true,
      })

      toast.success(
        `${result.enriched_count} prospects enrichis avec succès !\nCrédits débités: ${result.credits_deducted}`,
        { duration: 4000 }
      )

      // Reload history
      loadHistory()
    } catch (err) {
      toast.error(`Erreur: ${err.message}`)
    }
  }


  // Show initial loader only on first mount while history loads
  const isInitialLoad = historyLoading && searches.length === 0 && !results

  return (
    <div className="prospects-view">
      {isInitialLoad ? (
        <div className="empty-state">
          <div className="loading-spinner" style={{ width: 48, height: 48, border: '4px solid rgb(var(--rgb-brand) / 0.1)', borderTop: '4px solid rgb(var(--rgb-brand))', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
          <p style={{ marginTop: 16 }}>Chargement...</p>
        </div>
      ) : (
        <>
          <ProspectsSearchBox onSearch={handleSearch} loading={loading} />

          {error && (
            <div className="empty-state">
              <h3 style={{ color: '#ef4444' }}>Erreur</h3>
              <p>{error}</p>
            </div>
          )}

          {!results && !error && !loading && !currentQuery && !crmView && !isInitialLoad && <ProspectsEmptyState />}

          {crmView && (
            <>
              <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <p style={{ margin: 0, fontSize: 13, color: 'rgb(var(--rgb-ink) / 0.45)' }}>
                  {crmView.prospects.length} profil{crmView.prospects.length > 1 ? 's' : ''} trouvé{crmView.prospects.length > 1 ? 's' : ''} dans votre base
                  {loading && <span style={{ marginLeft: 8, color: 'rgb(var(--rgb-brand))' }}>· recherche de nouveaux en cours…</span>}
                </p>
                <button onClick={handleCloseCrmView} style={{ background: 'none', border: 'none', color: 'rgb(var(--rgb-ink) / 0.3)', cursor: 'pointer', fontSize: 13 }}>✕ Fermer</button>
              </div>
              <ProspectsTableCRM
                prospects={crmView.prospects}
                search={crmView.search}
                onBack={handleCloseCrmView}
                onEnrichSingle={handleEnrichSingle}
              />
            </>
          )}

          {currentQuery && localResults.length > 0 && (
            <div style={{ margin: '16px 0', padding: '14px 18px', borderRadius: 12, background: 'rgba(255,200,0,0.05)', border: '1px solid rgba(255,200,0,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 18 }}>🗂️</span>
                <div>
                  <p style={{ margin: 0, fontWeight: 600, color: 'rgba(255,200,0,0.9)', fontSize: 14 }}>
                    {localResults.length} profil{localResults.length > 1 ? 's' : ''} similaire{localResults.length > 1 ? 's' : ''} déjà dans votre base
                  </p>
                  <p style={{ margin: 0, color: 'rgb(var(--rgb-ink) / 0.4)', fontSize: 12 }}>
                    Ces profils sont déjà achetés — la recherche ci-dessous trouve de nouveaux contacts
                  </p>
                </div>
              </div>
              <button
                onClick={handleOpenLocalCrm}
                style={{ padding: '8px 16px', borderRadius: 8, background: 'rgba(255,200,0,0.08)', border: '1px solid rgba(255,200,0,0.2)', color: 'rgba(255,200,0,0.8)', fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}
              >
                Consulter →
              </button>
            </div>
          )}


          {loading && !results && (
            <SearchingAnimation query={currentQuery} progress={progress} found={found} />
          )}

          {showLoadingAnimation && results && (
            <LoadingAnimation targetNumber={results.total} query={currentQuery} onComplete={handleLoadingComplete} />
          )}

          {showPreview && results && results.prospects.length > 0 && (
            <>
              {admin && results.warnings?.length > 0 && (
                <div style={{ margin: '0 0 12px', padding: '10px 14px', borderRadius: 10, fontSize: 12, color: '#fcd34d', background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.25)' }}>
                  Sources indisponibles pendant cette recherche (visible par les admins) :
                  {results.warnings.map((w) => <div key={w}>· {w}</div>)}
                </div>
              )}
              <ProspectsPreviewGrid prospects={results.prospects} totalResults={results.total} />
              <EnrichmentSelector totalResults={results.total} onEnrich={handleEnrich} onCancel={handleCancel} disabled={enrichLoading} />
            </>
          )}

          <SearchHistory searches={searches} loading={historyLoading} onSelectSearch={handleSelectHistory} onBuyMore={handleBuyMore} />
        </>
      )}
    </div>
  )
}
